import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import {
    AddressFieldDisplayCollectionModel,
    BaseAddressModel,
    CountryModel,
    CustomerValidationDto,
    StateModel,
} from "@insite/client-framework/Types/ApiModels";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Select, { SelectPresentationProps, SelectProps } from "@insite/mobius/Select";
import TextField, { TextFieldPresentationProps, TextFieldProps } from "@insite/mobius/TextField";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import residentialAddressIndicatorHandler from "@tCentro/Handlers/ResidentialAddressIndicatorHandler";
import React, { ChangeEvent, ComponentType, useEffect, useState } from "react";
import useAppDispatch from "@insite/client-framework/Common/Hooks/useAppDispatch";
import { css } from "styled-components";
import getColor from "@insite/mobius/utilities/getColor";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { connect } from "react-redux";

const mapStateToProps = (state: ApplicationState) => ({
    siteName: state.context.website.name,
});
interface OwnProps {
    firstName: string;
    lastName: string;
    attention: string;
    companyName: string;
    address1: string;
    address2: string;
    address3: string;
    address4: string;
    country: BaseAddressModel["country"];
    state: BaseAddressModel["state"];
    city: string;
    postalCode: string;
    phone: string;
    email: string;
    fax: string;
    onChangeFirstName: TextFieldProps["onChange"];
    onChangeLastName: TextFieldProps["onChange"];
    onChangeAttention: TextFieldProps["onChange"];
    onChangeCompanyName: TextFieldProps["onChange"];
    onChangeAddress1: TextFieldProps["onChange"];
    onChangeAddress2: TextFieldProps["onChange"];
    onChangeAddress3: TextFieldProps["onChange"];
    onChangeAddress4: TextFieldProps["onChange"];
    onChangeCountry: (event: React.ChangeEvent<HTMLSelectElement>, country: BaseAddressModel["country"]) => void;
    onChangeState: (event: React.ChangeEvent<HTMLSelectElement>, state: BaseAddressModel["state"]) => void;
    onChangeCity: TextFieldProps["onChange"];
    onChangePostalCode: TextFieldProps["onChange"];
    onChangePhone: TextFieldProps["onChange"];
    onChangeEmail: TextFieldProps["onChange"];
    onChangeFax: TextFieldProps["onChange"];
    firstNameError: React.ReactNode;
    lastNameError: React.ReactNode;
    attentionError: React.ReactNode;
    companyNameError: React.ReactNode;
    address1Error: React.ReactNode;
    address2Error: React.ReactNode;
    address3Error: React.ReactNode;
    address4Error: React.ReactNode;
    countryError: React.ReactNode;
    stateError: React.ReactNode;
    cityError: React.ReactNode;
    postalCodeError: React.ReactNode;
    phoneError: React.ReactNode;
    emailError: React.ReactNode;
    faxError: React.ReactNode;
    countries: CountryModel[];
    fieldDisplay: AddressFieldDisplayCollectionModel;
    validation: CustomerValidationDto;
    extendedStyles?: CustomerAddressFormFieldStyles;
    hideAdditionalAddresses?: boolean;
    addressAutocomplete?: any[] | undefined;
    onAddressChange?: (searchValue: string) => void;
    onAddressSelect?: (address: any) => void;
    skipInitialResidentialAddressSync?: boolean;
}

type Props = ReturnType<typeof mapStateToProps> & OwnProps;

export interface CustomerAddressFormFieldStyles {
    form?: InjectableCss;
    container?: GridContainerProps;
    firstNameGridItem?: GridItemProps;
    firstNameText?: TextFieldPresentationProps;
    lastNameGridItem?: GridItemProps;
    lastNameText?: TextFieldPresentationProps;
    companyNameGridItem?: GridItemProps;
    companyNameText?: TextFieldPresentationProps;
    attentionGridItem?: GridItemProps;
    attentionText?: TextFieldPresentationProps;
    address1GridItem?: GridItemProps;
    address1Text?: TextFieldPresentationProps;
    address2GridItem?: GridItemProps;
    address2Text?: TextFieldPresentationProps;
    address3GridItem?: GridItemProps;
    address3Text?: TextFieldPresentationProps;
    address4GridItem?: GridItemProps;
    address4Text?: TextFieldPresentationProps;
    countryGridItem?: GridItemProps;
    countrySelect?: SelectPresentationProps;
    cityGridItem?: GridItemProps;
    cityText?: TextFieldPresentationProps;
    stateGridItem?: GridItemProps;
    stateSelect?: SelectPresentationProps;
    postalCodeGridItem?: GridItemProps;
    postalCodeText?: TextFieldPresentationProps;
    phoneGridItem?: GridItemProps;
    phoneText?: TextFieldPresentationProps;
    faxGridItem?: GridItemProps;
    faxText?: TextFieldPresentationProps;
    emailGridItem?: GridItemProps;
    emailText?: TextFieldPresentationProps;
    formButtonsWrapper?: InjectableCss;
    cancelButton?: ButtonPresentationProps;
    submitButton?: ButtonPresentationProps;
    addAddressLineLinkGridItem?: GridItemProps;
    addAddressLineLink?: LinkPresentationProps;
    addressTypeToggleWrapperStyles?: InjectableCss;
    addressTypeToggleStyles?: InjectableCss;
    addressTypeToggleButtonWrapperStyles?: InjectableCss;
    addressTypeToggleButtonStyles?: InjectableCss;
    addressToggleGridItem?: GridItemProps;
    addressList?: InjectableCss;
}

export const customerAddressFormFieldStyles: CustomerAddressFormFieldStyles = {
    firstNameGridItem: { width: 6 },
    lastNameGridItem: { width: 6 },
    companyNameGridItem: { width: 6 },
    attentionGridItem: { width: 6 },
    address1GridItem: {
        width: 12,
        css: css`
            position: relative;
        `,
    },
    address2GridItem: { width: 12 },
    address3GridItem: { width: 12 },
    address4GridItem: { width: 12 },
    countryGridItem: { width: 12 },
    addressToggleGridItem: { width: 12 },
    cityGridItem: { width: 12 },
    stateGridItem: { width: 6 },
    addAddressLineLinkGridItem: { width: 12 },
    postalCodeGridItem: { width: 6 },
    phoneGridItem: { width: 6 },
    faxGridItem: { width: 6 },
    emailGridItem: { width: 12 },
    addressList: {
        css: css`
            position: absolute;
            top: 100%;
            left: 0;
            background: white;
            width: calc(100% - 30px);
            z-index: 999;
            padding: 5px 0 0;
            margin: 0;
            border: 1px solid #ebebeb;
            border-radius: 4px;
            height: 200px;
            overflow: auto;
            margin: 0 15px 0;
            li {
                padding: 10px 15px;
                border-bottom: 1px solid #ebebeb;
                cursor: pointer;
                &:hover {
                    background-color: #f2f2f2;
                }
            }
        `,
    },
    formButtonsWrapper: {
        css: css`
            display: flex;
            justify-content: flex-end;
            margin-top: 30px;
        `,
    },
    cancelButton: {
        color: "secondary",
        css: css`
            margin-right: 10px;
        `,
    },
    addressTypeToggleWrapperStyles: {
        css: css`
            width: 400px;
            @media all and (max-width: 480px) {
                width: 100%;
            }
        `,
    },
    addressTypeToggleStyles: {
        css: css`
            font-family: Poppins;
            font-size: 14px;
            font-weight: 600;
            line-height: 16px;
            padding-left: 17px;
            color: #262525;
        `,
    },
    addressTypeToggleButtonWrapperStyles: {
        css: css`
            margin-top: 8px;
            display: flex;
            border-radius: 24px;
            overflow: hidden;
            border: 1px solid ${getColor("primary")};
        `,
    },
    addressTypeToggleButtonStyles: {
        css: css`
            padding: 16px 24px;
            border: none;
            cursor: pointer;
            position: relative;
            display: flex;
            align-items: center;
            flex: 0 0 50%;
            justify-content: center;
            border-radius: 0;
            min-height: 48px;
            font-size: 14px;
            line-height: 16px;
            font-weight: 600;
            & div {
                position: relative;
            }
            & svg {
                position: absolute;
                top: -6px;
                left: -22px;
            }
        `,
    },
};

const styles = customerAddressFormFieldStyles;

const CustomerAddressFormFields = ({
    siteName,
    firstName,
    lastName,
    attention,
    companyName,
    address1,
    address2,
    address3,
    address4,
    country,
    state,
    city,
    postalCode,
    phone,
    email,
    fax,
    onChangeFirstName,
    onChangeLastName,
    onChangeAttention,
    onChangeCompanyName,
    onChangeAddress1,
    onChangeAddress2,
    onChangeAddress3,
    onChangeAddress4,
    onChangeCountry,
    onChangeState,
    onChangeCity,
    onChangePostalCode,
    onChangePhone,
    onChangeEmail,
    onChangeFax,
    firstNameError,
    lastNameError,
    attentionError,
    companyNameError,
    address1Error,
    address2Error,
    address3Error,
    address4Error,
    countryError,
    stateError,
    cityError,
    postalCodeError,
    phoneError,
    emailError,
    faxError,
    countries,
    fieldDisplay,
    validation,
    extendedStyles,
    hideAdditionalAddresses,
    addressAutocomplete,
    onAddressChange,
    onAddressSelect,
    skipInitialResidentialAddressSync,
}: Props) => {
    const dispatch = useAppDispatch();
    const [styles] = React.useState(() => mergeToNew(customerAddressFormFieldStyles, extendedStyles));

    // Factory function to create email validation handlers
    const createEmailValidationHandler = (onChangeHandler: TextFieldProps["onChange"]) => {
        return (event: React.ChangeEvent<HTMLInputElement>) => {
            // Prevent email addresses - reject if @ symbol is present
            if (!event.target.value.includes("@")) {
                onChangeHandler && onChangeHandler(event);
            }
        };
    };

    // Create handlers for address fields
    const handleAddress1Change = (event: React.ChangeEvent<HTMLInputElement>) => {
        // Prevent email addresses - reject if @ symbol is present
        if (!event.target.value.includes("@")) {
            onChangeAddress1 && onChangeAddress1(event);
            // Trigger autocomplete when address1 changes
            onAddressChange?.(event.target.value);
        }
    };
    const handleAddress2Change = createEmailValidationHandler(onChangeAddress2);
    const handleAddress3Change = createEmailValidationHandler(onChangeAddress3);
    const handleAddress4Change = createEmailValidationHandler(onChangeAddress4);

    if (!countries) {
        return null;
    }

    const [selectedCountry, setSelectedCountry] = useState<CountryModel | undefined>(undefined);
    const [states, setStates] = useState<StateModel[]>([]);
    const [isAddress2Opened, setIsAddress2Opened] = useState(false);
    const [isAddress3Opened, setIsAddress3Opened] = useState(false);
    const [isAddress4Opened, setIsAddress4Opened] = useState(false);

    // Change addressType to boolean: false = Commercial, true = Residential
    const [ResidentialAddressIndicator, setResidentialAddressIndicator] = useState(false);

    useEffect(() => {
        dispatch({
            type: "Pages/OnePageCheckout/CENShppingAddressTypeState",
            ResidentialAddressIndicator: false,
        });
        if (!skipInitialResidentialAddressSync) {
            dispatch(residentialAddressIndicatorHandler(false));
        }
    }, [dispatch, skipInitialResidentialAddressSync]);

    useEffect(() => {
        if (countries.length === 1 && !country) {
            setSelectedCountry(countries[0]);
            onChangeCountry({} as ChangeEvent<HTMLSelectElement>, countries[0]);
        } else {
            setSelectedCountry(countries.find(c => c.id === country?.id));
        }
    }, [country]);

    useEffect(() => {
        setStates(selectedCountry?.states || []);
    }, [selectedCountry]);

    useEffect(() => {
        if (address2Error || address3Error || address4Error) {
            setIsAddress2Opened(true);
        }

        if (address3Error || address4Error) {
            setIsAddress3Opened(true);
        }

        if (address4Error) {
            setIsAddress4Opened(true);
        }
    }, [address2Error, address3Error, address4Error]);

    useEffect(() => {
        if (address2 !== "") {
            setIsAddress2Opened(true);
        } else {
            setIsAddress2Opened(false);
        }
    }, [address2]);

    const handleCountryChange: SelectProps["onChange"] = event => {
        const country = countries.find(c => c.id === event.target.value);
        setSelectedCountry(country);
        onChangeCountry(event, country || null);
    };

    const handleStateChange: SelectProps["onChange"] = event => {
        const state = selectedCountry?.states?.find(s => s.id === event.target.value);
        onChangeState(event, state || null);
    };

    const CheckIcon = () => (
        <div>
            <svg width="16" height="12" viewBox="0 0 16 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                    d="M15.875 1.5C15.875 1.78786 15.7651 2.07586 15.5454 2.29523L6.54541 11.2952C6.32656 11.516 6.03828 11.625 5.75 11.625C5.46172 11.625 5.17414 11.5151 4.95477 11.2954L0.454766 6.79541C0.235039 6.57656 0.125 6.28828 0.125 6C0.125 5.35734 0.650586 4.875 1.25 4.875C1.53786 4.875 1.82586 4.98486 2.04523 5.20459L5.75 8.91094L13.9555 0.705469C14.1734 0.485039 14.4617 0.375 14.75 0.375C15.3512 0.375 15.875 0.856641 15.875 1.5Z"
                    fill="white"
                />
            </svg>
        </div>
    );

    return (
        <GridContainer {...styles.container} data-test-selector="customerAddressFormFields" className="mf-form">
            <GridItem {...styles.addressToggleGridItem}>
                <StyledWrapper {...styles.addressTypeToggleWrapperStyles}>
                    <StyledWrapper {...styles.addressTypeToggleStyles}>{translate("Address Type*")}</StyledWrapper>
                    <StyledWrapper {...styles.addressTypeToggleButtonWrapperStyles}>
                        <Button
                            {...styles.addressTypeToggleButtonStyles}
                            onClick={e => {
                                e.preventDefault();
                                setResidentialAddressIndicator(false); // Commercial
                                // Correct dispatch for reducer in CENShippingAddressTypeReducerStore.ts
                                dispatch({
                                    type: "Pages/OnePageCheckout/CENShppingAddressTypeState",
                                    ResidentialAddressIndicator: false,
                                });
                                dispatch(residentialAddressIndicatorHandler(false));
                            }}
                            style={{
                                backgroundColor: !ResidentialAddressIndicator
                                    ? siteName === "Nisco"
                                        ? "#2B5197"
                                        : "#2d6a4f"
                                    : "#ffffff",
                                color: !ResidentialAddressIndicator ? "#ffffff" : "#666666",
                            }}
                        >
                            {!ResidentialAddressIndicator && <CheckIcon />}
                            {translate("Commercial")}
                        </Button>
                        <Button
                            {...styles.addressTypeToggleButtonStyles}
                            onClick={e => {
                                e.preventDefault();
                                setResidentialAddressIndicator(true); // Residential
                                dispatch({
                                    type: "Pages/OnePageCheckout/CENShppingAddressTypeState",
                                    ResidentialAddressIndicator: true,
                                });
                                dispatch(residentialAddressIndicatorHandler(true));
                            }}
                            style={{
                                backgroundColor: ResidentialAddressIndicator
                                    ? siteName === "Nisco"
                                        ? "#2B5197"
                                        : "#2d6a4f"
                                    : "#ffffff",
                                color: ResidentialAddressIndicator ? "#ffffff" : "#110c0cff",
                            }}
                        >
                            {ResidentialAddressIndicator && <CheckIcon />}
                            {translate("Residential")}
                        </Button>
                    </StyledWrapper>
                </StyledWrapper>
            </GridItem>
            {fieldDisplay.firstName!.isVisible && (
                <GridItem {...styles.firstNameGridItem}>
                    <TextField
                        {...styles.firstNameText}
                        name="firstName"
                        label={fieldDisplay.firstName!.displayName}
                        value={firstName}
                        disabled={validation.firstName!.isDisabled}
                        required={validation.firstName!.isRequired}
                        maxLength={validation.firstName!.maxLength || undefined}
                        onChange={onChangeFirstName}
                        error={firstNameError}
                        data-test-selector="customerAddressForm_firstName"
                    />
                </GridItem>
            )}
            {fieldDisplay.lastName!.isVisible && (
                <GridItem {...styles.lastNameGridItem}>
                    <TextField
                        {...styles.lastNameText}
                        name="lastName"
                        label={fieldDisplay.lastName!.displayName}
                        value={lastName}
                        disabled={validation.lastName!.isDisabled}
                        required={validation.lastName!.isRequired}
                        maxLength={validation.lastName!.maxLength || undefined}
                        onChange={onChangeLastName}
                        error={lastNameError}
                        data-test-selector="customerAddressForm_lastName"
                    />
                </GridItem>
            )}
            {fieldDisplay.companyName!.isVisible && (
                <GridItem {...styles.companyNameGridItem}>
                    <TextField
                        {...styles.companyNameText}
                        name="companyName"
                        label={fieldDisplay.companyName!.displayName}
                        value={companyName}
                        disabled={validation.companyName!.isDisabled}
                        required={validation.companyName!.isRequired}
                        maxLength={validation.companyName!.maxLength || undefined}
                        onChange={onChangeCompanyName}
                        error={companyNameError}
                        data-test-selector="customerAddressForm_companyName"
                    />
                </GridItem>
            )}
            {fieldDisplay.attention!.isVisible && (
                <GridItem {...styles.attentionGridItem}>
                    <TextField
                        {...styles.attentionText}
                        name="attention"
                        label={fieldDisplay.attention!.displayName}
                        value={attention}
                        disabled={validation.attention!.isDisabled}
                        required={validation.attention!.isRequired}
                        maxLength={validation.attention!.maxLength || undefined}
                        onChange={onChangeAttention}
                        error={attentionError}
                        data-test-selector="customerAddressForm_attention"
                    />
                </GridItem>
            )}
            {fieldDisplay.address1!.isVisible && (
                <GridItem {...styles.address1GridItem}>
                    <TextField
                        {...styles.address1Text}
                        name="address1"
                        label={fieldDisplay.address1!.displayName}
                        value={address1}
                        disabled={validation.address1!.isDisabled}
                        required={validation.address1!.isRequired}
                        maxLength={validation.address1!.maxLength || undefined}
                        onChange={handleAddress1Change}
                        error={address1Error}
                        data-test-selector="customerAddressForm_address1"
                    />
                    {addressAutocomplete && addressAutocomplete.length > 0 && (
                        <StyledWrapper as="ul" {...styles.addressList}>
                            {addressAutocomplete.map(address => (
                                <StyledWrapper
                                    as="li"
                                    key={address.street_line}
                                    onClick={() => onAddressSelect?.(address)}
                                    data-test-selector="customerAddressForm_autocomplete_suggestion"
                                >
                                    {`${address.street_line}, ${address.city}`}
                                </StyledWrapper>
                            ))}
                        </StyledWrapper>
                    )}
                </GridItem>
            )}
            {fieldDisplay.address2!.isVisible && (
                <>
                    {!isAddress2Opened && (
                        <GridItem {...styles.addAddressLineLinkGridItem}>
                            <Link
                                {...styles.addAddressLineLink}
                                onClick={() => setIsAddress2Opened(true)}
                                data-test-selector="customerAddressForm_addressLine2"
                            >
                                {translate("+ Address line 2")}
                            </Link>
                        </GridItem>
                    )}
                    {isAddress2Opened && (
                        <>
                            <GridItem {...styles.address2GridItem}>
                                <TextField
                                    {...styles.address2Text}
                                    name="address2"
                                    label={fieldDisplay.address2!.displayName}
                                    value={address2}
                                    disabled={validation.address2!.isDisabled}
                                    required={validation.address2!.isRequired}
                                    maxLength={validation.address2!.maxLength || undefined}
                                    onChange={handleAddress2Change}
                                    error={address2Error}
                                    data-test-selector="customerAddressForm_address2"
                                />
                            </GridItem>
                            <GridItem {...styles.address2GridItem}>
                                <Link
                                    {...styles.addAddressLineLink}
                                    onClick={() => setIsAddress2Opened(false)}
                                    data-test-selector="customerAddressForm_addressLine2"
                                >
                                    {translate("- Address line 2")}
                                </Link>
                            </GridItem>
                        </>
                    )}
                </>
            )}
            {fieldDisplay.address3!.isVisible && (
                <>
                    {hideAdditionalAddresses && isAddress2Opened && !isAddress3Opened && (
                        <GridItem {...styles.addAddressLineLinkGridItem}>
                            <Link {...styles.addAddressLineLink} onClick={() => setIsAddress3Opened(true)}>
                                {translate("+ Address line 3")}
                            </Link>
                        </GridItem>
                    )}
                    {(!hideAdditionalAddresses || isAddress3Opened) && (
                        <GridItem {...styles.address3GridItem}>
                            <TextField
                                {...styles.address3Text}
                                name="address3"
                                label={fieldDisplay.address3!.displayName}
                                value={address3}
                                disabled={validation.address3!.isDisabled}
                                required={validation.address3!.isRequired}
                                maxLength={validation.address3!.maxLength || undefined}
                                onChange={handleAddress3Change}
                                error={address3Error}
                                data-test-selector="customerAddressForm_address3"
                            />
                        </GridItem>
                    )}
                </>
            )}
            {fieldDisplay.address4!.isVisible && (
                <>
                    {hideAdditionalAddresses && isAddress3Opened && !isAddress4Opened && (
                        <GridItem {...styles.addAddressLineLinkGridItem}>
                            <Link {...styles.addAddressLineLink} onClick={() => setIsAddress4Opened(true)}>
                                {translate("+ Address line 4")}
                            </Link>
                        </GridItem>
                    )}
                    {(!hideAdditionalAddresses || isAddress4Opened) && (
                        <GridItem {...styles.address4GridItem}>
                            <TextField
                                {...styles.address4Text}
                                name="address4"
                                label={fieldDisplay.address4!.displayName}
                                value={address4}
                                disabled={validation.address4!.isDisabled}
                                required={validation.address4!.isRequired}
                                maxLength={validation.address4!.maxLength || undefined}
                                onChange={handleAddress4Change}
                                error={address4Error}
                                data-test-selector="customerAddressForm_address4"
                            />
                        </GridItem>
                    )}
                </>
            )}
            {fieldDisplay.country!.isVisible && countries.length > 1 && (
                <GridItem {...styles.countryGridItem}>
                    <Select
                        {...styles.countrySelect}
                        label={fieldDisplay.country!.displayName}
                        value={selectedCountry?.id || ""}
                        disabled={validation.country!.isDisabled || countries.length === 1}
                        required={validation.country!.isRequired}
                        onChange={handleCountryChange}
                        error={countryError}
                        data-test-selector="customerAddressForm_country"
                    >
                        <option value="">{translate("Select Country")}</option>
                        {countries.map(co => (
                            <option key={co.id} value={co.id}>
                                {co.name}
                            </option>
                        ))}
                    </Select>
                </GridItem>
            )}
            {fieldDisplay.city!.isVisible && (
                <GridItem {...styles.cityGridItem}>
                    <TextField
                        {...styles.cityText}
                        name="city"
                        label={fieldDisplay.city!.displayName}
                        value={city}
                        disabled={validation.city!.isDisabled}
                        required={validation.city!.isRequired}
                        maxLength={validation.city!.maxLength || undefined}
                        onChange={onChangeCity}
                        error={cityError}
                        data-test-selector="customerAddressForm_city"
                    />
                </GridItem>
            )}
            {states.length > 0 && (
                <GridItem {...styles.stateGridItem}>
                    <Select
                        {...styles.stateSelect}
                        label={fieldDisplay.state!.displayName}
                        value={state?.id || ""}
                        disabled={validation.state!.isDisabled}
                        required
                        onChange={handleStateChange}
                        error={stateError}
                        data-test-selector="customerAddressForm_state"
                    >
                        <option value="">{translate("Select State")}</option>
                        {states.map(st => (
                            <option key={st.id} value={st.id}>
                                {st.name}
                            </option>
                        ))}
                    </Select>
                </GridItem>
            )}
            {fieldDisplay.postalCode!.isVisible && (
                <GridItem {...styles.postalCodeGridItem}>
                    <TextField
                        {...styles.postalCodeText}
                        name="postalCode"
                        label={fieldDisplay.postalCode!.displayName}
                        value={postalCode}
                        disabled={validation.postalCode!.isDisabled}
                        required={validation.postalCode!.isRequired}
                        maxLength={validation.postalCode!.maxLength || undefined}
                        onChange={onChangePostalCode}
                        error={postalCodeError}
                        data-test-selector="customerAddressForm_postalCode"
                    />
                </GridItem>
            )}
            {fieldDisplay.phone!.isVisible && (
                <GridItem {...styles.phoneGridItem}>
                    <TextField
                        {...styles.phoneText}
                        name="phone"
                        label={fieldDisplay.phone!.displayName}
                        value={phone}
                        disabled={validation.phone!.isDisabled}
                        required={validation.phone!.isRequired}
                        maxLength={validation.phone!.maxLength || undefined}
                        onChange={onChangePhone}
                        error={phoneError}
                        data-test-selector="customerAddressForm_phone"
                    />
                </GridItem>
            )}
            {fieldDisplay.fax!.isVisible && (
                <GridItem {...styles.faxGridItem}>
                    <TextField
                        {...styles.faxText}
                        name="fax"
                        label={fieldDisplay.fax!.displayName}
                        value={fax}
                        disabled={validation.fax!.isDisabled}
                        required={validation.fax!.isRequired}
                        maxLength={validation.fax!.maxLength || undefined}
                        onChange={onChangeFax}
                        error={faxError}
                        data-test-selector="customerAddressForm_fax"
                    />
                </GridItem>
            )}
            {fieldDisplay.email!.isVisible && (
                <GridItem {...styles.emailGridItem}>
                    <TextField
                        {...styles.emailText}
                        name="email"
                        label={fieldDisplay.email!.displayName}
                        value={email}
                        disabled={validation.email!.isDisabled}
                        required={validation.email!.isRequired}
                        maxLength={validation.email!.maxLength || undefined}
                        onChange={onChangeEmail}
                        error={emailError}
                        data-test-selector="customerAddressForm_email"
                    />
                </GridItem>
            )}
        </GridContainer>
    );
};

export { CustomerAddressFormFields };
export default connect(mapStateToProps, null)(CustomerAddressFormFields as any) as any;
